<!--    Modal for creating CompoundWaterMeters  -->
<!--Copy right - © 2024 tecera gmbh-->

<script>
    import { fly, fade } from "svelte/transition";
    import { BASE_API_URL } from "../../config.js";
    import CsvDatePicker from "../CsvDatePicker.svelte";
    import {
        all_compound_water_meters,
        toast_message,
        all_hymos,
    } from "../../stores.js";
    import SegmentControl from "./form_parts/SegmentControl.svelte";
    import { get_device_list_csv_data } from "../../scripts/data.js";
    import { find_main_meter } from "../../scripts/utils.js";
    import JSZip from "jszip";
    import LoaderModal from "./LoaderModal.svelte";
    import { onDestroy } from "svelte";

    export let is_open; // Determines whether the modal is open or not
    export let multiple_devices = true;

    let device_type_name_single = "Gerät";
    let device_type_name_multiple = "Geräte";

    if (multiple_devices) {
        device_type_name_single = "Wasserzähler";
        device_type_name_multiple = "Wasserzähler";
    }

    /**
     * Title of the modal
     * @type {String}
     */
    let modal_title = "CSV-Download";

    /**
     * Determines whether the modal can be closed
     * @type {Boolean}
     */
    let can_modal_close = false;

    /**
     * The selected devices for multi selection
     * @type {Array}
     */
    let selected_devices_multiple = [];

    /**
     * Determines whether the  device selection is open
     * @type {Boolean}
     */
    let is_device_selection_dropdown_open = false;

    /**
     * Determines whether the compression dropdown is open
     * @type {Boolean}
     */
    let is_compression_dropdown_open = false;

    /**
     * Determines whether the interval dropdown is open
     * @type {Boolean}
     */
    let is_interval_dropdown_open = false;
    /**
     * Deep copy of the device configs, to be able to manipulate it
     * @type {Array}
     */
    let device_configs_copy = [];

    /**
     * List of all unique channel measurands
     * @type {Array<String>}
     */
    let measurand_list = [];

    /**
     * List of all unselected channels measurands
     * @type {Array<String>}
     */
    let unselected_measurand_list = [];

    /**
     * Available compression options for the drowdown
     * @type {Array<Dict>}
     */
    let compression_options = [
        { id: 1, name: "Minutenwerte", resample_time: "1min",db_name:"MINUTE" },
        { id: 2, name: "Stundenwerte", resample_time: "1h",db_name:"HOUR" },
        { id: 3, name: "Tageswerte", resample_time: "1d",db_name:"DAY" },
        { id: 4, name: "Wochenwerte", resample_time: "1w",db_name:"WEEK" },
    ];

    /**
     * Available interval options for the drowdown
     * @type {Array<Dict>}
     */
     let interval_options = [
        { id: 1, name: "Tagesbericht",  value: "d"},
        { id: 2, name: "Wochenbericht", value: "w" },
        { id: 3, name: "Monatsbericht", value: "m"},
        { id: 4, name: "Jahresbericht", value: "y" },
    ];

    /**
     * The currently selected compression option
     * @type {Dict}
     */
    let selected_compression_option = compression_options[1];

        /**
     * The currently selected interval option
     * @type {Dict}
     */
     let selected_interval_option = interval_options[0];

    /**
     * Wether the download button is disabled
     * @type {Boolean}
     */
    let is_download_button_disabled = false;

    /**
     * Wether the loader modal is open
     * @type {Boolean}
     */
    let is_loader_modal_open = false;

    /**
     * The message being displayed in the loader modal
     * @type {String}
     */
    let current_download_step = "";

    /**
     * The start date for the CSV download
     * @type {Object}
     */
    let start_date = moment().subtract(2, "weeks");

    /**
     * The end date for the CSV download
     * @type {Object}
     */
    let end_date = moment();

    /**
     * Wether a single CSV or multiple CSVs are being created
     * @type {Boolean}
     */
    let is_creating_single_csv = false;

    /**
     * Wether a csv or a reportconfig is being created
     * @type {Boolean}
     */
    let is_creating_reportconfig = false;

    /**
     * The abort controller to cancel the fetch request
     * @type {Object}
     */
    let abort_controller;

    /**
     * Wether the select all devices checkbox is selected
     */
    let is_select_all_devices_selected = false;

    /**
     * Create a deep copy of the compound water meters and add the main meter to each device or take all devices if not multiple
     */
    function create_device_config_copy() {
        if (multiple_devices) {
            if ($all_compound_water_meters) {
                device_configs_copy = JSON.parse(
                    JSON.stringify($all_compound_water_meters),
                );
                device_configs_copy.forEach((device) => {
                    let main_meter = find_main_meter(device);
                    device.main_meter = main_meter;
                    device.ignored_measurands = []; // add ignored_measurands property to change Measurands on a per device level
                    device.watermeterconfigs.forEach((config) => {
                        config.ignored_measurands = [];
                    });
                });
            }
        } else {
            if ($all_hymos) {
                device_configs_copy = JSON.parse(JSON.stringify($all_hymos));
                device_configs_copy.forEach((device_config) => {
                    device_config.ignored_measurands = []; // add ignored_measurands property to change Measurands on a per device level
                });
            }
        }
    }

    /**
     * Close the modal and reset values to default
     */
    function close_modal() {
        if (can_modal_close) {
            is_open = false;
            selected_devices_multiple = [];
            can_modal_close = false;
            is_device_selection_dropdown_open = false;
            is_compression_dropdown_open = false;
            is_interval_dropdown_open = false;
            unselected_measurand_list = [];
            selected_compression_option = compression_options[1];
            selected_interval_option = interval_options[0];
            is_creating_single_csv = false;
            current_download_step = "";
            is_select_all_devices_selected = false;
            is_sub_modal_open = false;
        }
    }

    /**
     * Handling of opening the menu and the eventlistener for closing outside of it
     */
    function handle_menu_open() {
        if (is_device_selection_dropdown_open) {
            is_device_selection_dropdown_open = false;
            document.body.removeEventListener("click", handle_menu_close);
        } else {
            is_compression_dropdown_open = false;
            is_interval_dropdown_open = false;
            is_device_selection_dropdown_open = true;
            document.body.addEventListener("click", handle_menu_close);
        }
    }

    /**
     * Handling of closing the menu and the eventlistener for closing outside of it
     */
    function handle_menu_close() {
        is_device_selection_dropdown_open = false;
        document.body.removeEventListener("click", handle_menu_close);
    }

    /**
     * Handling of opening the menu and the eventlistener for closing outside of it
     */
    function handle_compression_menu_open() {
        if (is_compression_dropdown_open) {
            is_compression_dropdown_open = false;
            document.body.removeEventListener(
                "click",
                handle_compression_menu_close,
            );
        } else {
            is_device_selection_dropdown_open = false;
            is_interval_dropdown_open = false;
            is_compression_dropdown_open = true;
            document.body.addEventListener(
                "click",
                handle_compression_menu_close,
            );
        }
    }

    /**
     * Handling of closing the menu and the eventlistener for closing outside of it
     */
    function handle_compression_menu_close() {
        is_compression_dropdown_open = false;
        document.body.removeEventListener(
            "click",
            handle_compression_menu_close,
        );
    }

    /**
    * Handling of opening the menu and the eventlistener for closing outside of it
    */
    function handle_interval_menu_open() {
        if (is_interval_dropdown_open) {
            is_interval_dropdown_open = false;
            document.body.removeEventListener(
                "click",
                handle_interval_menu_close,
            );
        } else {
            is_device_selection_dropdown_open = false;
            is_compression_dropdown_open = false;
            is_interval_dropdown_open = true;
            document.body.addEventListener(
                "click",
                handle_interval_menu_close,
            );
        }
    }

    /**
     * Handling of closing the menu and the eventlistener for closing outside of it
     */
    function handle_interval_menu_close() {
        is_interval_dropdown_open = false;
        document.body.removeEventListener(
            "click",
            handle_interval_menu_close,
        );
    }

    /**
     * Handling of selecting or unselecting a water meter in the multi selection
     * @param {Object} water_meter - The selected water meter
     */
    function handle_multi_select(water_meter) {
        if (selected_devices_multiple.includes(water_meter)) {
            let temp = selected_devices_multiple.filter(
                (item) => item !== water_meter,
            );
            selected_devices_multiple = temp;
        } else {
            selected_devices_multiple = [
                ...selected_devices_multiple,
                water_meter,
            ];
        }
    }

    /**
     * Check if a water meter or its sub meters has a channel with a specific measurand
     * @param {Object} water_meter - The water meter to check
     * @param {String} measurand - The measurand to check for
     * @returns {Boolean} True if the water meter has the channel, false otherwise
     */
    function does_water_meter_have_channel(water_meter, measurand) {
        return water_meter.watermeterconfigs.some((config) =>
            config.channels.some(
                (water_meter_channel) =>
                    water_meter_channel.measurand === measurand,
            ),
        );
    }

    function does_device_have_channel(device, measurand) {
        return device.channels.some(
            (channel) => channel.measurand === measurand,
        );
    }

    /**
     * Toggle the selection of a measurand for all devices, and selects/unselects the measurand
     * @param {String} measurand - The measurand to toggle
     */
    function toggle_global_measurand_selection(measurand) {
        if (unselected_measurand_list.includes(measurand)) {
            // Remove the measurand from the unselected list
            unselected_measurand_list = unselected_measurand_list.filter(
                (item) => item !== measurand,
            );
            device_configs_copy.forEach((device) => {
                // Remove the measurand from the ignored_measurands list of all devices
                device.ignored_measurands = device.ignored_measurands.filter(
                    (item) => item !== measurand,
                );

                if (multiple_devices) {
                    device.watermeterconfigs.forEach((config) => {
                        config.ignored_measurands =
                            config.ignored_measurands.filter(
                                (item) => item !== measurand,
                            );
                    });
                }
            });
        } else {
            unselected_measurand_list = [
                ...unselected_measurand_list,
                measurand,
            ]; // Add the measurand to the unselected list
            device_configs_copy.forEach((device) => {
                // Add the measurand to the ignored_measurands list of all devices
                if (!device.ignored_measurands.includes(measurand)) {
                    device.ignored_measurands = [
                        ...device.ignored_measurands,
                        measurand,
                    ];
                    if (multiple_devices) {
                        device.watermeterconfigs.forEach((config) => {
                            config.ignored_measurands = [
                                ...config.ignored_measurands,
                                measurand,
                            ];
                        });
                    }
                }
            });
        }
    }

    /**
     * Toggle the selection of a measurand for a specific device
     * @param {Object} device - The device to toggle the measurand for
     * @param {String} measurand - The measurand to toggle
     */
    function toggle_device_measurand_selection(device, measurand) {
        if (multiple_devices) {
            if (device.ignored_measurands.includes(measurand)) {
                device.ignored_measurands = device.ignored_measurands.filter(
                    (item) => item !== measurand,
                );
                device.watermeterconfigs.forEach((config) => {
                    config.ignored_measurands =
                        config.ignored_measurands.filter(
                            (item) => item !== measurand,
                        );
                });
                selected_devices_multiple = [...selected_devices_multiple];
            } else {
                device.ignored_measurands = [
                    ...device.ignored_measurands,
                    measurand,
                ];
                device.watermeterconfigs.forEach((config) => {
                    config.ignored_measurands = [
                        ...config.ignored_measurands,
                        measurand,
                    ];
                });
                selected_devices_multiple = [...selected_devices_multiple];
            }
        } else {
            if (device.ignored_measurands.includes(measurand)) {
                device.ignored_measurands = device.ignored_measurands.filter(
                    (item) => item !== measurand,
                );
                selected_devices_multiple = [...selected_devices_multiple];
            } else {
                device.ignored_measurands = [
                    ...device.ignored_measurands,
                    measurand,
                ];
                selected_devices_multiple = [...selected_devices_multiple];
            }
        }
    }

    /**
     * Check if a measurand is selected for a specific device
     * @param {Object} device - The device to check
     * @param {String} measurand - The measurand to check
     * @returns {Boolean} True if the measurand is selected, false otherwise
     */
    function is_measurand_selected_for_device(device, measurand) {
        return !device.ignored_measurands.includes(measurand);
    }

    /**
     * Check if a channel is selected
     * @param channel - The channel to check
     */
    function is_channel_selected(channel) {
        return !unselected_measurand_list.includes(channel);
    }

    $: {
        if (is_open) {
            create_device_config_copy(); // Trigger function upon opening the modal
            get_reports();
            document.body.style.overflow = "hidden";
            document.body.classList.add("modal-open");
            setTimeout(() => {
                can_modal_close = true; // 250ms delay to prevent closing the modal immediately
            }, 250);
        } else {
            setTimeout(() => {
                document.body.style.overflow = "auto";
                document.body.classList.remove("modal-open");
            }, 350); // enable scrolling after the modal is closed
        }
    }

    // Updating the Channel list upon selecting or unselecting a water meter
    //TODO: Refactor into a function
    $: {
        if (multiple_devices) {
            if (
                selected_devices_multiple.length > 0 &&
                unselected_measurand_list
            ) {
                let channels = selected_devices_multiple.flatMap(
                    (water_meter) =>
                        water_meter.watermeterconfigs.flatMap((config) =>
                            config.channels.map((channel) => channel.measurand),
                        ),
                );
                measurand_list = [...new Set(channels)];
            } else {
                measurand_list = [];
            }
        } else {
            if (
                selected_devices_multiple.length > 0 &&
                unselected_measurand_list
            ) {
                let channels = selected_devices_multiple.flatMap((device) =>
                    device.channels.map((channel) => channel.measurand),
                );
                measurand_list = [...new Set(channels)];
            } else {
                measurand_list = [];
            }
        }
    }

    /**
     * Make and API call to get the CSV data then call the csv creation function @see create_csv
     */
    async function get_csv_data() {
        // abort controller to cancel API call
        abort_controller = new AbortController();
        const signal = abort_controller.signal;
        is_loader_modal_open = true;
        current_download_step = "Frage Daten vom Server ab...";
        let id_list = selected_devices_multiple.map((device) => device.id);
        let full_csv_data;
        if (multiple_devices) {
            full_csv_data = await get_device_list_csv_data(
                id_list,
                new Date(start_date),
                new Date(end_date),
                selected_compression_option.resample_time,
                signal,
            );
        current_download_step = "Erstelle CSV...";

            console.log("got data")
        } else {
            full_csv_data = await get_device_list_csv_data(
                id_list,
                new Date(start_date),
                new Date(end_date),
                selected_compression_option.resample_time,
                signal,
                "hymo",
            );
        }
        // in case the download got canceled we return
        if (full_csv_data === false) {
            $toast_message = " CSV-Download wurde abgebrochen";
            return;
        }
        if (multiple_devices) {
            create_csv(full_csv_data);
        } else {
            create_csv_hymo(full_csv_data);
        }
        current_download_step = "Lade CSV-Datei herunter...";
        is_loader_modal_open = false;
        current_download_step = "";
    }

    /**
     * Function to toggle the selection of all devices
     */
    function toggle_select_all_devices() {
        if (is_select_all_devices_selected) {
            selected_devices_multiple = [];
        } else {
            selected_devices_multiple = device_configs_copy;
        }
        is_select_all_devices_selected = !is_select_all_devices_selected;
    }

    // Cleanup on component destroy
    onDestroy(() => {
        if (abort_controller) {
            abort_controller.abort();
        }
    });

    function create_deep_copy(json) {
        return JSON.parse(JSON.stringify(json));
    }

    // TODO: Refactor this monster of a function
    function create_csv(full_csv_data) {
        console.log("Creating CSV")
        current_download_step = "Formatiere CSV-Datei...";
        // create a deep copy of the current channel selection to prevent changes while the data is being processed
        let selected_devices_multiple_copy = create_deep_copy(
            selected_devices_multiple,
        );
        let water_meter_configs_copy = [];
        selected_devices_multiple_copy.map((item) =>
            item.watermeterconfigs.forEach((config) =>
                water_meter_configs_copy.push(config),
            ),
        );
        // append the ignored_measurands to the full_csv_data we got from the server
        full_csv_data.forEach((compound_csv_data) => {
            // each compound
            compound_csv_data[0].forEach((water_meter_config_data) => {
                // each data object of the compound
                let device = water_meter_configs_copy.find(
                    (item) => item.id === water_meter_config_data.config.id,
                );
                if (device) {
                    water_meter_config_data.config.ignored_measurands =
                        device.ignored_measurands;
                }
            });
        });
        console.log("Appended Measurands")
        // loop through the list of all compounds
        let timestamps = new Set(); // Set to store all timestamps for a combined CSV
        let headers = ["MET/MEST", "longtime", "hour", "day", "month", "year"]; // Default headers for the CSV
        let rows = [];

        // for a single csv we need a set of all timestamps with data points
        if (is_creating_single_csv) {
            full_csv_data.forEach((compound_csv_data) => {
                compound_csv_data[0][0].data.forEach((data_point) => {
                    if (timestamps.has(data_point["received_at"])) {
                    }
                    timestamps.add(data_point["received_at"]);
                });
            });
            timestamps = Array.from(timestamps).sort();
        }
        if (!is_creating_single_csv) {
            const zip = new JSZip(); // Create a new ZIP file
            console.log("Creating empty ZIP file")
            full_csv_data.forEach((compound_csv_data) => {
                let csv_name =
                    compound_csv_data[0][0].config.sub_id +
                    "_" +
                    compound_csv_data[0][0].config.name;
                // reset the headers and rows for each compound
                let headers = [
                    "MET/MEST",
                    "longtime",
                    "hour",
                    "day",
                    "month",
                    "year",
                ];
                let rows = [];
                // loop through the list of compounds to get the headers for each config and set headers based on channels
                compound_csv_data[0].forEach((water_meter_config_data) => {
                    water_meter_config_data.config.channels.forEach(
                        (channel) => {
                            if (
                                !water_meter_config_data.config.ignored_measurands.includes(
                                    channel.measurand,
                                )
                            ) {
                                headers.push(
                                    water_meter_config_data.config.name +
                                        " " +
                                        channel.measurand +
                                        " " +
                                        water_meter_config_data.config.sub_id,
                                );
                            }
                        },
                    );
                });

                // loop through the list of data points for any config (Dates are set to be the same in the backend)
                compound_csv_data[0][0].data.forEach(
                    (data_point, data_point_index) => {
                        let date_parts = formate_date_to_dict(
                            data_point["received_at"],
                        );
                        let date = date_parts.formatted;
                        // Convert to long time format (milliseconds since the Unix epoch)
                        let receivedAtLongTime = date_parts.date.getTime();
                        let datapoints = [];
                        // loop through the list of compounds to get the data for each datapoint
                        compound_csv_data[0].forEach(
                            (water_meter_config_data) => {
                                water_meter_config_data.config.channels.forEach(
                                    (channel, idx) => {
                                        if (
                                            !water_meter_config_data.config.ignored_measurands.includes(
                                                channel.measurand,
                                            )
                                        ) {
                                            datapoints.push(
                                                water_meter_config_data.data[
                                                    data_point_index
                                                ][channel.name]
                                                    ? water_meter_config_data.data[
                                                          data_point_index
                                                      ][channel.name]
                                                          .toString()
                                                          .replace(/\./g, ",")
                                                    : "" + idx ===
                                                        water_meter_config_data
                                                            .config.channels
                                                            .length -
                                                            1
                                                      ? ";"
                                                      : "",
                                            );
                                        }
                                    },
                                );
                            },
                        );
                        if (compound_csv_data[1].length > 0) {
                            if (
                                !compound_csv_data[0][0].config.ignored_measurands.includes(
                                    "Zählerstand",
                                )
                            ) {
                                datapoints.push(
                                    compound_csv_data[1][data_point_index][
                                        "meter_reading_sum"
                                    ]
                                        .toString()
                                        .replace(/\./g, ","),
                                );
                            }
                            if (
                                !compound_csv_data[0][0].config.ignored_measurands.includes(
                                    "Volumenstrom",
                                )
                            ) {
                                datapoints.push(
                                    compound_csv_data[1][data_point_index][
                                        "volume_flow_sum"
                                    ]
                                        .toString()
                                        .replace(/\./g, ","),
                                );
                            }
                        }
                        let row =
                            date +
                            ";" +
                            receivedAtLongTime +
                            ";" +
                            date_parts.hour +
                            ";" +
                            date_parts.day +
                            ";" +
                            date_parts.month +
                            ";" +
                            date_parts.year +
                            ";" +
                            datapoints.join(";");

                        rows.push(row);
                    },
                );
                if (compound_csv_data[1].length > 0) {
                    if (
                        !compound_csv_data[0][0].config.ignored_measurands.includes(
                            "Zählerstand",
                        )
                    ) {
                        headers.push(
                            compound_csv_data[0][0].config.name +
                                " Zaehlerstand Gesamt",
                        );
                    }
                    if (
                        !compound_csv_data[0][0].config.ignored_measurands.includes(
                            "Volumenstrom",
                        )
                    ) {
                        headers.push(
                            compound_csv_data[0][0].config.name +
                                " Volumenstrom Gesamt",
                        );
                    }
                }
                const csvHeader = headers.join(";") + "\n";
                const csvContent = csvHeader + rows.join("\n");
                const bom = "\uFEFF"; // Add BOM to the beginning of the CSV content - NEEDED FOR UTF8 !!!
                const fileContent = bom + csvContent;
                const blob = new Blob([fileContent], {
                    type: "text/csv;charset=utf-8;",
                });

                // Add the Blob to the ZIP file
                zip.file(`export_${csv_name}.csv`, blob);
                console.log("Added CSV file to ZIP");
            });

            // Generate the ZIP file and trigger the download
            zip.generateAsync({ type: "blob" }).then((content) => {
                const url = URL.createObjectURL(content);
                const link = document.createElement("a");
                link.href = url;
                link.download = `CSV-Download__${formate_date_to_dict(new Date(moment())).csv_formatted}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            });
            console.log("ZIP file created and download triggered");
            $toast_message = " CSV-Datei erfolgreich erstellt";
        } else {
            //loop through all the data - we get single compounds here
            full_csv_data.forEach((compound_csv_data) => {
                // loop through the list of compounds to get the headers for each config and set headers based on channels
                compound_csv_data[0].forEach((water_meter_config_data) => {
                    //TODO: maybe get these headers and then sort them by measurand
                    water_meter_config_data.config.channels.forEach(
                        (channel) => {
                            if (
                                !water_meter_config_data.config.ignored_measurands.includes(
                                    channel.measurand,
                                )
                            ) {
                                headers.push(
                                    water_meter_config_data.config.name +
                                        " " +
                                        channel.measurand +
                                        " " +
                                        water_meter_config_data.config.sub_id,
                                );
                            }
                        },
                    );
                });
                if (compound_csv_data[1].length > 0) {
                    if (
                        !compound_csv_data[0][0].config.ignored_measurands.includes(
                            "Zählerstand",
                        )
                    ) {
                        headers.push(
                            compound_csv_data[0][0].config.name +
                                " Zaehlerstand Gesamt",
                        );
                    }
                    if (
                        !compound_csv_data[0][0].config.ignored_measurands.includes(
                            "Volumenstrom",
                        )
                    ) {
                        headers.push(
                            compound_csv_data[0][0].config.name +
                                " Volumenstrom Gesamt",
                        );
                    }
                }
            });
            // loop through the list of data points for any config (Dates are set to be the same in the backend)
            timestamps.forEach((data_point) => {
                //Creation of date points in the csv
                let date_parts = formate_date_to_dict(data_point);
                let date = date_parts.formatted;
                let receivedAtLongTime = date_parts.date.getTime();
                let datapoints = [];

                // loop through the list of compounds to get the data for each datapoint
                full_csv_data.forEach((compound_csv_data) => {
                    let current_sum_data_index;
                    // loop through the list of compounds to get a compound
                    compound_csv_data[0].forEach((water_meter_config_data) => {
                        // loop through the  compound to get each device

                        // default data points?
                        let device_push_data = [];
                        water_meter_config_data.config.channels.forEach(
                            (channel) => {
                                if (
                                    !water_meter_config_data.config.ignored_measurands.includes(
                                        channel.measurand,
                                    )
                                ) {
                                    device_push_data.push("");
                                }
                            },
                        );

                        water_meter_config_data.data.forEach(
                            // loop through existing datapoints for this device
                            (item, item_index) => {
                                if (item["received_at"] === data_point) {
                                    // if a matching datapoint is found we get the data
                                    device_push_data = []; // reset to get actual data
                                    water_meter_config_data.config.channels.forEach(
                                        (channel) => {
                                            if (
                                                !water_meter_config_data.config.ignored_measurands.includes(
                                                    channel.measurand,
                                                )
                                            ) {
                                                device_push_data.push(
                                                    item[channel.name]
                                                        ? item[channel.name]
                                                              .toString()
                                                              .replace(
                                                                  /\./g,
                                                                  ",",
                                                              )
                                                        : "",
                                                );
                                            }
                                        },
                                    );
                                    current_sum_data_index = item_index;
                                }
                            },
                        );
                        device_push_data.forEach((item) => {
                            datapoints.push(item);
                        });
                    });
                    if (compound_csv_data[1].length > 0) {
                        if (
                            !compound_csv_data[0][0].config.ignored_measurands.includes(
                                "Zählerstand",
                            )
                        ) {
                            datapoints.push(
                                compound_csv_data[1][current_sum_data_index]
                                    ? compound_csv_data[1][
                                          current_sum_data_index
                                      ]["meter_reading_sum"]
                                          .toString()
                                          .replace(/\./g, ",")
                                    : "",
                            );
                        }
                        if (
                            !compound_csv_data[0][0].config.ignored_measurands.includes(
                                "Volumenstrom",
                            )
                        ) {
                            datapoints.push(
                                compound_csv_data[1][current_sum_data_index]
                                    ? compound_csv_data[1][
                                          current_sum_data_index
                                      ]["volume_flow_sum"]
                                          .toString()
                                          .replace(/\./g, ",")
                                    : "",
                            );
                        }
                    }
                });
                let row =
                    date +
                    ";" +
                    receivedAtLongTime +
                    ";" +
                    date_parts.hour +
                    ";" +
                    date_parts.day +
                    ";" +
                    date_parts.month +
                    ";" +
                    date_parts.year +
                    ";" +
                    datapoints.join(";");

                rows.push(row);
            });

            const csvHeader = headers.join(";") + "\n";

            const csvContent = csvHeader + rows.join("\n");

            // Add BOM to the beginning of the CSV content - NEEDED FOR UTF8 !!!
            const bom = "\uFEFF";
            const blob = new Blob([bom + csvContent], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);

            // Create an anchor element and set properties for downloading
            let downloadLink = document.createElement("a");
            downloadLink.href = url;
            downloadLink.download = `CSV-Download__${formate_date_to_dict(new Date(moment())).csv_formatted}.csv`;

            // Programmatically click the anchor to trigger the download
            downloadLink.click();

            // Cleanup: revoke the object URL to free up resources
            URL.revokeObjectURL(url);
            $toast_message = " CSV-Datei erfolgreich erstellt";
        }
        close_modal();
    }

    // TODO: Refactor this monster of a function
    function create_csv_hymo(full_csv_data) {
        console.log("full_csv_data", full_csv_data);
        current_download_step = "Formatiere CSV-Datei...";
        // create a deep copy of the current channel selection to prevent changes while the data is being processed
        let selected_devices_multiple_copy = create_deep_copy(
            selected_devices_multiple,
        );
        let hymo_configs_copy = selected_devices_multiple_copy;
        console.log("hymo_configs_copy", hymo_configs_copy);

        // append the ignored_measurands to the full_csv_data we got from the server
        full_csv_data.forEach((hymo_csv_data) => {
            // each compound
            // each data object of the compound
            let device = hymo_configs_copy.find(
                (item) => item.id === hymo_csv_data.config.id,
            );
            if (device) {
                hymo_csv_data.config.ignored_measurands =
                    device.ignored_measurands;
            }
        });
        console.log("full_csv_data - 2", full_csv_data);

        // loop through the list of all compounds
        let timestamps = new Set(); // Set to store all timestamps for a combined CSV
        let headers = ["MET/MEST", "longtime", "hour", "day", "month", "year"]; // Default headers for the CSV
        let rows = [];

        // for a single csv we need a set of all timestamps with data points
        if (is_creating_single_csv) {
            full_csv_data.forEach((compound_csv_data) => {
                compound_csv_data.data.forEach((data_point) => {
                    if (timestamps.has(data_point["received_at"])) {
                    }
                    timestamps.add(data_point["received_at"]);
                });
            });
            timestamps = Array.from(timestamps).sort();
        }
        console.log("timestamps", timestamps);
        if (!is_creating_single_csv) {
            const zip = new JSZip(); // Create a new ZIP file
            full_csv_data.forEach((hymo_config_data) => {
                let csv_name = hymo_config_data.config.name;
                // reset the headers and rows for each compound
                let headers = [
                    "MET/MEST",
                    "longtime",
                    "hour",
                    "day",
                    "month",
                    "year",
                ];
                let rows = [];
                // loop through each hymo config to get the headers
                hymo_config_data.config.channels.forEach((channel) => {
                    if (
                        !hymo_config_data.config.ignored_measurands.includes(
                            channel.measurand,
                        )
                    ) {
                        headers.push(
                            hymo_config_data.config.name +
                                " " +
                                channel.measurand,
                        );
                    }
                });
                console.log("headers", headers);

                // loop through the list of data points for any config (Dates are set to be the same in the backend)
                hymo_config_data.data.forEach(
                    (data_point, data_point_index) => {
                        let date_parts = formate_date_to_dict(
                            data_point["received_at"],
                        );
                        let date = date_parts.formatted;
                        // Convert to long time format (milliseconds since the Unix epoch)
                        let receivedAtLongTime = date_parts.date.getTime();
                        let datapoints = [];
                        // loop through the list of compounds to get the data for each datapoint
                        hymo_config_data.config.channels.forEach(
                            (channel, idx) => {
                                if (
                                    !hymo_config_data.config.ignored_measurands.includes(
                                        channel.measurand,
                                    )
                                ) {
                                    datapoints.push(
                                        hymo_config_data.data[data_point_index][
                                            channel.name
                                        ] != null
                                            ? hymo_config_data.data[
                                                  data_point_index
                                              ][channel.name]
                                                  .toString()
                                                  .replace(/\./g, ",")
                                            : "" + idx ===
                                                hymo_config_data.config.channels
                                                    .length -
                                                    1
                                              ? ";"
                                              : "",
                                    );
                                }
                            },
                        );

                        let row =
                            date +
                            ";" +
                            receivedAtLongTime +
                            ";" +
                            date_parts.hour +
                            ";" +
                            date_parts.day +
                            ";" +
                            date_parts.month +
                            ";" +
                            date_parts.year +
                            ";" +
                            datapoints.join(";");

                        rows.push(row);
                    },
                );
                const csvHeader = headers.join(";") + "\n";
                const csvContent = csvHeader + rows.join("\n");
                const bom = "\uFEFF"; // Add BOM to the beginning of the CSV content - NEEDED FOR UTF8 !!!
                const fileContent = bom + csvContent;
                const blob = new Blob([fileContent], {
                    type: "text/csv;charset=utf-8;",
                });

                // Add the Blob to the ZIP file
                zip.file(`export_${csv_name}.csv`, blob);
            });

            // Generate the ZIP file and trigger the download
            zip.generateAsync({ type: "blob" }).then((content) => {
                const url = URL.createObjectURL(content);
                const link = document.createElement("a");
                link.href = url;
                link.download = `CSV-Download__${formate_date_to_dict(new Date(moment())).csv_formatted}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            });
            $toast_message = " CSV-Datei erfolgreich erstellt";
        } else {
            //loop through all the data - we get single configs here
            full_csv_data.forEach((compound_csv_data) => {
                // loop through the list of compounds to get the headers for each config and set headers based on channels
                compound_csv_data.config.channels.forEach((channel) => {
                    if (
                        !compound_csv_data.config.ignored_measurands.includes(
                            channel.measurand,
                        )
                    ) {
                        headers.push(
                            compound_csv_data.config.name +
                                " " +
                                channel.measurand,
                        );
                    }
                });
            });
            // loop through the list of data points for any config (Dates are set to be the same in the backend)
            timestamps.forEach((data_point) => {
                //Creation of date points in the csv
                let date_parts = formate_date_to_dict(data_point);
                let date = date_parts.formatted;
                let receivedAtLongTime = date_parts.date.getTime();
                let datapoints = [];

                // loop through the list of compounds to get the data for each datapoint
                full_csv_data.forEach((compound_csv_data) => {
                    // default data points?
                    let device_push_data = [];
                    compound_csv_data.config.channels.forEach((channel) => {
                        if (
                            !compound_csv_data.config.ignored_measurands.includes(
                                channel.measurand,
                            )
                        ) {
                            device_push_data.push("");
                        }
                    });

                    compound_csv_data.data.forEach(
                        // loop through existing datapoints for this device
                        (item, item_index) => {
                            if (item["received_at"] === data_point) {
                                // if a matching datapoint is found we get the data
                                device_push_data = []; // reset to get actual data
                                compound_csv_data.config.channels.forEach(
                                    (channel) => {
                                        if (
                                            !compound_csv_data.config.ignored_measurands.includes(
                                                channel.measurand,
                                            )
                                        ) {
                                            device_push_data.push(
                                                item[channel.name]
                                                    ? item[channel.name]
                                                          .toString()
                                                          .replace(/\./g, ",")
                                                    : "",
                                            );
                                        }
                                    },
                                );
                            }
                        },
                    );
                    device_push_data.forEach((item) => {
                        datapoints.push(item);
                    });
                });
                let row =
                    date +
                    ";" +
                    receivedAtLongTime +
                    ";" +
                    date_parts.hour +
                    ";" +
                    date_parts.day +
                    ";" +
                    date_parts.month +
                    ";" +
                    date_parts.year +
                    ";" +
                    datapoints.join(";");

                rows.push(row);
            });

            const csvHeader = headers.join(";") + "\n";

            const csvContent = csvHeader + rows.join("\n");

            // Add BOM to the beginning of the CSV content - NEEDED FOR UTF8 !!!
            const bom = "\uFEFF";
            const blob = new Blob([bom + csvContent], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);

            // Create an anchor element and set properties for downloading
            let downloadLink = document.createElement("a");
            downloadLink.href = url;
            downloadLink.download = `CSV-Download__${formate_date_to_dict(new Date(moment())).csv_formatted}.csv`;

            // Programmatically click the anchor to trigger the download
            downloadLink.click();

            // Cleanup: revoke the object URL to free up resources
            URL.revokeObjectURL(url);
            $toast_message = " CSV-Datei erfolgreich erstellt";
        }
        close_modal();
    }

    /**
     * Function to format a timestamp to a date string and also get its datecomponents.
     * @param {timestamp} timestamp - a timestamp that should be formatted
     * @returns {Object} - An Object containing the formatted time, aswell as each date component
     */
    function formate_date_to_dict(timestamp) {
        let date = new Date(timestamp);
        let day = String(date.getDate()).padStart(2, "0");
        let month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based in JS
        let year = date.getFullYear();
        let hours = String(date.getHours()).padStart(2, "0");
        let minutes = String(date.getMinutes()).padStart(2, "0");
        return {
            date: date,
            formatted:
                day + "." + month + "." + year + " " + hours + ":" + minutes,
            csv_formatted:
                year + "-" + month + "-" + day + "_" + hours + "-" + minutes,
            day: day,
            month: month,
            year: year,
            hour: hours,
        };
    }

    // reactive disabling of the download button
    $: {
        if (selected_devices_multiple.length === 0) {
            is_download_button_disabled = true;
        } else {
            is_download_button_disabled = false;
        }
    }

    async function create_reportconfig() {
        tried_submitting = true;
        if (!email_valid){
            return;
        }
        let url_extension = `/daily_reports/`;
        let request_url = BASE_API_URL + url_extension;

        let post_data = {
            email: email.trim(),
            csv_mode: is_creating_single_csv ? "SINGLE" : "MULTIPLE",
            compression: selected_compression_option.db_name,
            selected_devices_multiple: selected_devices_multiple,
            interval: selected_interval_option.value,
        };
        try {
            let response = await fetch(request_url, {
                method: "POST",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(post_data),
            });
            if (response.status == 201) {
                $toast_message = " Tagesbericht erfolgreich abonniert";
                get_reports();
                tried_submitting = false;
                email = "";
                close_modal();
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }


    // change list of available compressions to be smaller than the selected interval
    $: {
        if (is_creating_reportconfig) {
            if (selected_interval_option.id==1){
                if (selected_compression_option.id == 4){
                    selected_compression_option = compression_options[1];
                }

                compression_options = [
                    { id: 1, name: "Minutenwerte", resample_time: "1min",db_name:"MINUTE" },
                    { id: 2, name: "Stundenwerte", resample_time: "1h",db_name:"HOUR" },
                    { id: 3, name: "Tageswerte", resample_time: "1d",db_name:"DAY" },
                ];
            } else {
                compression_options = [
                { id: 1, name: "Minutenwerte", resample_time: "1min",db_name:"MINUTE" },
                { id: 2, name: "Stundenwerte", resample_time: "1h",db_name:"HOUR" },
                { id: 3, name: "Tageswerte", resample_time: "1d",db_name:"DAY" },
                { id: 4, name: "Wochenwerte", resample_time: "1w",db_name:"WEEK" },
            ];

            }

        } else {
            compression_options = [
                { id: 1, name: "Minutenwerte", resample_time: "1min",db_name:"MINUTE" },
                { id: 2, name: "Stundenwerte", resample_time: "1h",db_name:"HOUR" },
                { id: 3, name: "Tageswerte", resample_time: "1d",db_name:"DAY" },
                { id: 4, name: "Wochenwerte", resample_time: "1w",db_name:"WEEK" },
            ];
        }
    }


    

    let email = "";
    let email_valid = false;
    let tried_submitting = false;
    // Regex for email
    const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    function validate_email(email) {
        return EMAIL_REGEX.test(email.trim().toLowerCase());
    }


    // Reactive validation of inputs
    $: email_valid = validate_email(email);
    let is_sub_modal_open = false;
    function close_sub_modal() {
        is_sub_modal_open = false;
    }
    let report_list = [];

    async function delete_report(report){
        let url_extension = `/daily_reports/${report.id}`;
        let request_url = BASE_API_URL + url_extension;

        try {
            let response = await fetch(request_url, {
                method: "DELETE",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
            });
            if (response.status == 200) {
                report_list = report_list.filter(item => item.id !== report.id);
                $toast_message = " Bericht erfolgreich gelöscht";
                return true;
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    }
    async function get_reports(){
        let url_extension = `/daily_reports/`;
        let request_url = BASE_API_URL + url_extension;

        try {
            let response = await fetch(request_url, {
                method: "GET",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
            });
            if (response.status == 200) {
                response = await response.json();
                report_list = response.data;
                return true;
            } else {
                report_list = [];
                return false;
            }
        } catch (error) {
            return false;
        }
    }
</script>

{#if is_open}
    <div
        class="svelte-modal svelte-backdrop"
        tabindex="-1"
        role="dialog"
        on:mousedown|self={() => close_modal()}
        on:keydown={(e) => {
            if (e.key === "Escape") {
                close_modal();
            }
        }}
        transition:fade={{ duration: 150 }}
    >
        <div
            class="modal-dialog modal-dialog-centered"
            role="document"
            style="max-width: 720px;"
            in:fly={{ y: -400, duration: 350, delay: 100 }}
            out:fly={{ y: -400, duration: 350 }}
        >
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="deleteTilte">
                        {modal_title}
                    </h5>
                    <button
                        type="button"
                        class="close"
                        on:click={() => close_modal()}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body" style="height:840px;overflow:auto;">
                    <div
                        Style="display:flex;flex-direction:column;height:100%;width:100%;"
                    >
                        <div style="flex: 0 0 10%;">
                            {#if selected_devices_multiple.length >= 8 || end_date - start_date >= 1000 * 60 * 60 * 24 * 180}
                                <div class="alert alert-warning" role="alert">
                                    <i class="fa-solid fa-triangle-exclamation"
                                    ></i>&nbsp;&nbsp;Das Erstellen der CSV-Datei
                                    kann bei vielen Geräten oder einem großen
                                    Zeitraum etwas länger dauern.
                                </div>
                            {/if}
                            {#if multiple_devices}
                            <p class="p-modal pl-0 mt-2 mb-1">Modus:</p>
                            <!--    TODO: Implement Permissions here    -->
                            <SegmentControl
                                bind:toggle_variable={is_creating_reportconfig}
                                toggle_label_0="Direker Download"
                                toggle_label_1="E-mail Bericht erstellen"
                            />
                            {/if}
                            <p class="p-modal pl-0 mt-4 mb-1">
                                CSV Erstellung:
                            </p>
                            <SegmentControl
                                bind:toggle_variable={is_creating_single_csv}
                                toggle_label_0="eine Datei pro Gerät"
                                toggle_label_1="alle Geräte in einer Datei"
                            />
                            {#if !is_creating_reportconfig}
                                <p class="p-modal pl-0 mt-4 mb-1">Zeitraum:</p>
                                <div
                                    class="input-group"
                                    style="justify-content:center;"
                                >
                                    <CsvDatePicker
                                        bind:start_date
                                        bind:end_date
                                    />
                                </div>
                            {:else}
                            <p class="p-modal pl-0 mt-4 mb-1">Intervall:</p>
                            <div class="input-group">
                                <button
                                    class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                    id="compression_id"
                                    type="button"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    on:click|stopPropagation={handle_interval_menu_open}
                                >
                                    {selected_interval_option.name}
                                </button>
                                <!-- Sensor Dropdown -->
                                <div
                                    class="dropdown-menu svelte-dropdown filter_dropdown mt-2 {is_interval_dropdown_open
                                        ? 'show'
                                        : ''}"
                                    aria-labelledby="compression_id"
                                >
                                    <div
                                        class="filter_dropdown_scrollable"
                                        id="compression_id_scrollable"
                                    >
                                        {#each interval_options as interval_option}
                                            <button
                                                class="btn btn-transparent btn-block dropdown__checkbox"
                                                style="text-transform:none;"
                                                on:click={() =>
                                                    (selected_interval_option =
                                                    interval_option)}
                                                >{interval_option.name}</button
                                            >
                                        {/each}
                                    </div>
                                </div>
                            </div>
                            {/if}
                            <p class="p-modal pl-0 mt-4 mb-1">
                                {device_type_name_multiple}:
                            </p>
                            <div class="input-group">
                                <button
                                    class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                    id="geraete_id"
                                    type="button"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    on:click|stopPropagation={handle_menu_open}
                                >
                                    {selected_devices_multiple.length > 0
                                        ? selected_devices_multiple.length +
                                          (selected_devices_multiple.length == 1
                                              ? " " +
                                                device_type_name_single +
                                                " ausgewählt"
                                              : " " +
                                                device_type_name_multiple +
                                                " ausgewählt")
                                        : device_type_name_multiple + " wählen"}
                                </button>
                                <!-- Sensor Dropdown -->
                                <div
                                    class="dropdown-menu svelte-dropdown filter_dropdown mt-2 {is_device_selection_dropdown_open
                                        ? 'show'
                                        : ''}"
                                    aria-labelledby="geraete_id"
                                >
                                    <div
                                        class="filter_dropdown_scrollable"
                                        id="alle_geraete_ids"
                                    >
                                        <label
                                            on:keydown={() => {}}
                                            on:click|stopPropagation
                                            class="dropdown__checkbox dropdown__label"
                                        >
                                            <input
                                                on:click|stopPropagation={() =>
                                                    toggle_select_all_devices()}
                                                type="checkbox"
                                                class="checkbox_test"
                                                checked={is_select_all_devices_selected}
                                            />
                                            Alle {device_type_name_multiple}</label
                                        >
                                        {#if multiple_devices}
                                            {#each device_configs_copy.sort((a, b) => parseInt(a.main_meter.sub_id) - parseInt(b.main_meter.sub_id)) as compound_water_meter}
                                                <label
                                                    on:keydown={() => {}}
                                                    on:click|stopPropagation
                                                    class="dropdown__checkbox dropdown__label"
                                                >
                                                    <input
                                                        on:click|stopPropagation={() =>
                                                            handle_multi_select(
                                                                compound_water_meter,
                                                            )}
                                                        type="checkbox"
                                                        class="checkbox_test"
                                                        checked={selected_devices_multiple.includes(
                                                            compound_water_meter,
                                                        )}
                                                    />
                                                    {compound_water_meter
                                                        .main_meter.sub_id +
                                                        " - " +
                                                        compound_water_meter
                                                            .main_meter
                                                            .name}</label
                                                >
                                            {/each}
                                        {:else}
                                            {#each device_configs_copy.sort( (a, b) => a.name.localeCompare(b.name), ) as device_config}
                                                <label
                                                    on:keydown={() => {}}
                                                    on:click|stopPropagation
                                                    class="dropdown__checkbox dropdown__label"
                                                >
                                                    <input
                                                        on:click|stopPropagation={() =>
                                                            handle_multi_select(
                                                                device_config,
                                                            )}
                                                        type="checkbox"
                                                        class="checkbox_test"
                                                        checked={selected_devices_multiple.includes(
                                                            device_config,
                                                        )}
                                                    />
                                                    {device_config.name}</label
                                                >
                                            {/each}
                                        {/if}
                                    </div>
                                </div>
                            </div>
                            <p class="p-modal pl-0 mt-4 mb-1">Komprimierung:</p>
                            <div class="input-group">
                                <button
                                    class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                    id="compression_id"
                                    type="button"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    on:click|stopPropagation={handle_compression_menu_open}
                                >
                                    {selected_compression_option.name}
                                </button>
                                <!-- Sensor Dropdown -->
                                <div
                                    class="dropdown-menu svelte-dropdown filter_dropdown mt-2 {is_compression_dropdown_open
                                        ? 'show'
                                        : ''}"
                                    aria-labelledby="compression_id"
                                >
                                    <div
                                        class="filter_dropdown_scrollable"
                                        id="compression_id_scrollable"
                                    >
                                        {#each compression_options as compression_option}
                                            <button
                                                class="btn btn-transparent btn-block dropdown__checkbox"
                                                style="text-transform:none;"
                                                on:click={() =>
                                                    (selected_compression_option =
                                                        compression_option)}
                                                >{compression_option.name}</button
                                            >
                                        {/each}
                                    </div>
                                </div>
                            </div>
                            {#if is_creating_reportconfig}
                            <p class="p-modal pl-0 mt-4 mb-1">Empfänger:</p>
                            <input
                            bind:value={email}
                            class="form-control py-1 {tried_submitting
                                ? email_valid
                                    ? 'is-valid'
                                    : 'is-invalid'
                                : ''}"
                            style="border-width: 2px"
                            type="text"
                            placeholder="Bsp: M.Mustermann@ewe-armaturen.de"
                            aria-label="Search"
                            maxlength="64"
                            autocomplete="off"
                            id="email_input"
                        />
                        {#if tried_submitting && !email_valid}
                        <div class="invalid-feedback" style="display:block; font-size:90%;">
                            Bitten eine gültige Email Adresse eingeben.
                        </div>
                    {/if}
                            {/if}
                        </div>

                        <div style="flex: 1 1 70%;">
                            {#if selected_devices_multiple.length == 0}
                                <div class="col-12 full center-flex-box">
                                    <p class="h1 p-kachel-2">
                                        Keine {device_type_name_multiple} ausgewählt
                                    </p>
                                </div>
                            {:else}
                                <p class="p-modal pl-0 mb-1 mt-4">
                                    Messspuren:
                                </p>
                                <div class="user_box2" style="overflow:auto;">
                                    <table
                                        class="table table-striped table-sm"
                                        id="data_table"
                                    >
                                        <thead
                                            class="thead-light"
                                            id="data_table_head"
                                        >
                                            <tr>
                                                <th
                                                    scope="col"
                                                    style="position:sticky;top:-1px;background-color: #707070;color:white;border-left:1px solid  #707070;"
                                                    >{device_type_name_multiple}</th
                                                >

                                                {#each measurand_list as channel}
                                                    {#if is_channel_selected(channel)}
                                                        <th
                                                            class="table-head-btn"
                                                            scope="col"
                                                            on:click={() =>
                                                                toggle_global_measurand_selection(
                                                                    channel,
                                                                )}
                                                            ><i
                                                                class="fa-solid fa-square-check"
                                                            ></i><br />{channel}
                                                        </th>
                                                    {:else}
                                                        <th
                                                            class="table-head-btn"
                                                            scope="col"
                                                            on:click={() =>
                                                                toggle_global_measurand_selection(
                                                                    channel,
                                                                )}
                                                            ><i
                                                                class="fa-solid fa-square-xmark"
                                                            ></i><br />{channel}
                                                        </th>
                                                    {/if}
                                                {/each}
                                            </tr>
                                        </thead>
                                        <tbody id="customer_table_body">
                                            {#if multiple_devices}
                                                {#each selected_devices_multiple as water_meter (water_meter.id)}
                                                    <tr>
                                                        <th
                                                            style="border: 1px solid #dee2e6; vertical-align: middle;user-select:none;"
                                                            >{water_meter
                                                                .main_meter
                                                                .sub_id} - {water_meter
                                                                .main_meter
                                                                .name}</th
                                                        >

                                                        {#each measurand_list as channel}
                                                            <td
                                                                class="table-element-btn {does_water_meter_have_channel(
                                                                    water_meter,
                                                                    channel,
                                                                )
                                                                    ? ''
                                                                    : 'table-element-disabled'}"
                                                                style="border: 1px solid #dee2e6; vertical-align: middle;text-align:center;"
                                                                on:mouseenter={(
                                                                    e,
                                                                ) => {
                                                                    if (
                                                                        e.buttons ==
                                                                        1
                                                                    ) {
                                                                        toggle_device_measurand_selection(
                                                                            water_meter,
                                                                            channel,
                                                                        );
                                                                    }
                                                                }}
                                                                on:mousedown={() =>
                                                                    toggle_device_measurand_selection(
                                                                        water_meter,
                                                                        channel,
                                                                    )}
                                                                on:keydown={() => {}}
                                                                on:focus={() => {}}
                                                            >
                                                                <span
                                                                    hidden={!is_measurand_selected_for_device(
                                                                        water_meter,
                                                                        channel,
                                                                    ) ||
                                                                        !does_water_meter_have_channel(
                                                                            water_meter,
                                                                            channel,
                                                                        )}
                                                                >
                                                                    <i
                                                                        class="fa-solid fa-check"
                                                                        style="color:green;"
                                                                    ></i></span
                                                                >
                                                                <span
                                                                    hidden={is_measurand_selected_for_device(
                                                                        water_meter,
                                                                        channel,
                                                                    ) ||
                                                                        !does_water_meter_have_channel(
                                                                            water_meter,
                                                                            channel,
                                                                        )}
                                                                >
                                                                    <i
                                                                        class="fa-solid fa-xmark"
                                                                        style="color:red;"
                                                                    ></i></span
                                                                >
                                                                <span
                                                                    hidden={does_water_meter_have_channel(
                                                                        water_meter,
                                                                        channel,
                                                                    )}
                                                                >
                                                                    <i
                                                                        class="fa-solid fa-xmark"
                                                                        style="color:grey;"
                                                                    ></i>
                                                                </span>
                                                            </td>
                                                        {/each}
                                                    </tr>
                                                {/each}
                                            {:else}
                                                {#each selected_devices_multiple as device_config (device_config.id)}
                                                    <tr>
                                                        <th
                                                            style="border: 1px solid #dee2e6; vertical-align: middle;user-select:none;"
                                                            >{device_config.name}</th
                                                        >

                                                        {#each measurand_list as channel}
                                                            <td
                                                                class="table-element-btn {does_device_have_channel(
                                                                    device_config,
                                                                    channel,
                                                                )
                                                                    ? ''
                                                                    : 'table-element-disabled'}"
                                                                style="border: 1px solid #dee2e6; vertical-align: middle;text-align:center;"
                                                                on:mouseenter={(
                                                                    e,
                                                                ) => {
                                                                    if (
                                                                        e.buttons ==
                                                                        1
                                                                    ) {
                                                                        toggle_device_measurand_selection(
                                                                            device_config,
                                                                            channel,
                                                                        );
                                                                    }
                                                                }}
                                                                on:mousedown={() =>
                                                                    toggle_device_measurand_selection(
                                                                        device_config,
                                                                        channel,
                                                                    )}
                                                                on:keydown={() => {}}
                                                                on:focus={() => {}}
                                                            >
                                                                <span
                                                                    hidden={!is_measurand_selected_for_device(
                                                                        device_config,
                                                                        channel,
                                                                    ) ||
                                                                        !does_device_have_channel(
                                                                            device_config,
                                                                            channel,
                                                                        )}
                                                                >
                                                                    <i
                                                                        class="fa-solid fa-check"
                                                                        style="color:green;"
                                                                    ></i></span
                                                                >
                                                                <span
                                                                    hidden={is_measurand_selected_for_device(
                                                                        device_config,
                                                                        channel,
                                                                    ) ||
                                                                        !does_device_have_channel(
                                                                            device_config,
                                                                            channel,
                                                                        )}
                                                                >
                                                                    <i
                                                                        class="fa-solid fa-xmark"
                                                                        style="color:red;"
                                                                    ></i></span
                                                                >
                                                                <span
                                                                    hidden={does_device_have_channel(
                                                                        device_config,
                                                                        channel,
                                                                    )}
                                                                >
                                                                    <i
                                                                        class="fa-solid fa-xmark"
                                                                        style="color:grey;"
                                                                    ></i>
                                                                </span>
                                                            </td>
                                                        {/each}
                                                    </tr>
                                                {/each}
                                            {/if}
                                        </tbody>
                                    </table>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
                <!-- Modal Footer -->
                <div class="modal-footer mt-2">
                    {#if multiple_devices}
                    <button 
                        type="button" 
                        class="btn btn-primary mr-auto"
                        on:click={() => (is_sub_modal_open = true)}
                        >Tagesberichte</button
                    >
                    {/if}
                    <button
                        type="button"
                        class="btn btn-secondary"
                        on:click={() => close_modal()}>Abbrechen</button
                    >
                    {#if !is_creating_reportconfig}
                        <button
                            type="button"
                            class="btn btn-primary"
                            disabled={is_download_button_disabled}
                            on:click={() => get_csv_data()}
                            >CSV Herunterladen</button
                        >
                    {:else}
                        <button
                            type="button"
                            class="btn btn-primary"
                            disabled={is_download_button_disabled}
                            on:click={() => create_reportconfig()}
                            >Bericht abonnieren</button
                        >
                    {/if}
                </div>
            </div>
        </div>
    </div>
    <LoaderModal
        bind:is_open={is_loader_modal_open}
        title="CSV wird heruntergeladen"
        bind:current_download_step
        {abort_controller}
    />

   <!-- Submodal for reports -->

    {#if is_sub_modal_open}
    <div
        class="svelte-modal svelte-backdrop"
        tabindex="-1"
        role="dialog"
        transition:fade={{ duration: 150 }}
        on:click|self={() => close_sub_modal()}
        on:keydown={(e) => {
            if (e.key === "Escape") {
                close_sub_modal();
            }
        }}
    >
        <div
            class="modal-dialog modal-dialog-centered"
            role="document"
            in:fly={{ y: -400, duration: 350, delay: 100 }}
            out:fly={{ y: -400, duration: 350 }}
        >
            <div class="modal-content" style="max-height:70vh;">
                <div class="modal-header">
                    <h5 class="modal-title">Tagesberichte</h5>
                    <button
                        type="button"
                        class="close"
                        on:click={() => close_sub_modal()}
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body" style="overflow:auto;">
                    <table class="table table-striped table-sm">
                        <tbody>
                            {#each report_list as report}
                                <tr
                                    ><td class="align-middle"
                                        >{report.email} ---- Bericht ID:{report.id}</td
                                    ><td class="col-md-1"
                                        ><button
                                            on:click={() =>
                                                delete_report(report)}
                                            class="btn bg-transparent btn-clear"
                                            style="margin-left:auto;"
                                            ><i class="fa fa-trash-can"
                                            ></i></button
                                        ></td
                                    ></tr
                                >
                            {/each}
                        </tbody>
                    </table>
                </div>
                <!-- Modal Footer -->
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-primary"
                        on:click={() => close_sub_modal()}>Zurück</button
                    >
                </div>
            </div>
        </div>
    </div>
    {/if}
{/if}
